import { useTranslation } from "next-i18next";
import { ImageWithTranslation } from "@common/types/ImageWithTranslation";
import classNames from "classnames";
import {
  CarPlusShieldEn,
  CarPlusShieldFr,
} from "@public/images/purchase/coverage";
import { useRouter } from "next/router";
import LocalImage from "@common/components/LocalImage";
import { ExtendedWarrantVehicleDisplayDetails } from "@modules/purchase/warranty/types/ExtendedWarrantyRates";
import { Button } from "@talladega/components";
import IALogoWhite from "@public/images/ia-logo-white.svg";
import KijijiLogoWhite from "@public/images/kijiji-logo-white.svg";
import AutoCanLogoWhite from "@public/images/autocan-logo-white.svg";
import WarrantyVehicleInfoCard from "./WarrantyVehicleInfoCard";

type Props = {
  isRatesAvailable: boolean;
  vehicleCoverImage: string | null | undefined;
  vehicleDisplayDetails?: ExtendedWarrantVehicleDisplayDetails;
  onLookUpWarrantyBtnClick: (element: string) => void;
};

const vehicleDefaultImage: ImageWithTranslation = {
  srcEn: CarPlusShieldEn,
  srcFr: CarPlusShieldFr,
  altKey: "purchase:alt_wty_vehicle_img",
};

const WarrantySelectCoverageHeading = ({
  isRatesAvailable,
  vehicleCoverImage,
  vehicleDisplayDetails,
  onLookUpWarrantyBtnClick,
}: Props) => {
  const { t } = useTranslation(["purchase", "common"]);
  const locale = useRouter().locale || "en";

  return (
    <div
      id="warranty-select-coverage-header-var-1"
      className="no-scrollbar relative overflow-visible bg-cover bg-center"
    >
      <div
        className={classNames(
          "relative mx-auto flex max-w-page flex-col-reverse items-center px-4 pb-6 pt-10 lg:flex-row lg:px-5 lg:py-16",
          {
            "lg:pb-0 lg:pt-16": isRatesAvailable,
            "gap-4": !!vehicleCoverImage,
          }
        )}
      >
        <div className="flex-1 lg:basis-[50%] lg:px-0">
          <div
            className={classNames(
              "flex flex-col text-white px-4 md:px-5 lg:max-w-[30rem]",
              {
                "gap-6": isRatesAvailable,
              }
            )}
          >
            {isRatesAvailable ? (
              <>
                <p className="font-semibold uppercase">
                  {t("purchase:coverage_made_simple")}
                </p>
                <h1 className="text-4xl">
                  {t("purchase:this_vehicle_qualifies_for_warranty")}
                </h1>
                <p>{t("purchase:pay_monthly_for_peace_of_mind")}</p>
              </>
            ) : (
              <>
                <div className="w-full items-start justify-start text-left">
                  <h1 className="text-2xl leading-[2.625rem] text-white md:text-5xl md:leading-[3.625rem]">
                    {t("common:enjoy_your_ride_piece_of_mind")}
                  </h1>
                </div>
                <div className="mt-4 flex w-full flex-col gap-4 lg:mt-6 items-start justify-start text-left">
                  <p className="text-gray-100 text-base font-medium md:text-lg">
                    {t("common:were_here_to_ensure_youre_covered")}
                  </p>
                </div>
                {/** Brands Logo Section */}
                <div className="flex flex-wrap sm:flex-row sm:items-center mt-4 lg:mb-[8px] w-full">
                  <span className="text-sm font-semibold text-gray-50 w-full sm:w-auto mb-2 sm:mb-0 sm:mr-1">
                    {t("common:in_association_with")}
                  </span>

                  <div className="flex flex-wrap justify-start items-center w-full sm:w-auto">
                    <LocalImage
                      className="h-auto w-12 lg:w-[75px] flex-none mb-[1px] px-2 lg:mb-[-5px]"
                      src={KijijiLogoWhite}
                      alt="Kijiji Logo"
                      priority
                    />
                    <hr className="border-l border-gray-200 h-[25px]" />

                    <LocalImage
                      className="h-auto w-12 lg:w-[75px] flex-none mb-[1px] px-2 lg:mb-[-5px]"
                      src={IALogoWhite}
                      alt="IA Logo"
                      priority
                    />
                    <hr className="border-l border-gray-200 h-[25px] mx-3" />

                    <LocalImage
                      className="h-auto w-28 lg:w-[120px] flex-none mb-[1px] lg:mb-[-5px]"
                      src={AutoCanLogoWhite}
                      alt="Autocan Logo"
                      priority
                    />
                  </div>
                </div>
              </>
            )}

            {isRatesAvailable ? (
              <div className="flex items-center justify-center sm:px-0 md:px-16 lg:px-0">
                {vehicleDisplayDetails && (
                  <WarrantyVehicleInfoCard
                    vehicleDetails={vehicleDisplayDetails}
                    className="text-white bg-gray-800"
                    ctaElements={
                      <div
                        className="text-xs md:text-sm underline mr-auto px-0 cursor-pointer text-white"
                        onClick={() =>
                          onLookUpWarrantyBtnClick("Quote-PlanConfigurator")
                        }
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            onLookUpWarrantyBtnClick("Quote-PlanConfigurator");
                          }
                        }}
                      >
                        {t("purchase:quote_different_vehicle")}
                      </div>
                    }
                  />
                )}
              </div>
            ) : (
              <Button
                className="w-fit"
                onClick={() =>
                  onLookUpWarrantyBtnClick("Quote-PlanConfigurator")
                }
              >
                {t("purchase:get_a_quote")}
              </Button>
            )}
          </div>
        </div>
        <div className="hidden lg:block relative w-full flex-1 lg:basis-[50%]">
          <div className="relative inset-0 min-w-full">
            <div className="relative overflow-auto h-80 md:h-96 lg:h-[35rem]">
              <LocalImage
                className="z-[2] h-auto w-full rounded-lg object-contain"
                src={
                  locale === "fr"
                    ? vehicleDefaultImage.srcFr
                    : vehicleDefaultImage.srcEn
                }
                alt={t(vehicleDefaultImage.altKey)}
                fill
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarrantySelectCoverageHeading;
